import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { PopulatedInteraction } from '@momentum/types';
import { InteractionType } from '@momentum/types';
import { UserSelectorAvatar } from '@/shared/components/user-selector/UserSelectorAvatar';
import { InteractionTypeBadge } from '@/features/interactions/table/components/InteractionTypeBadge';
import { formatDefaultDate } from '@/shared/utils/date-utils';
import { ArrowRight, Calendar, Users } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useEmployees } from '@/shared/hooks/useEmployees';
import { PraiseReviewDrawer } from '@/features/wizard/components/PraiseReviewDrawer';
import { FeedbackReviewDrawer } from '@/features/wizard/components/FeedbackReviewDrawer';
import { Badge } from '@/shared/components/ui/Badge';
import {
  isNewInteraction,
  isOpenBlockage,
} from '../utils/interaction-highlights.utils';

interface GoalInteractionsListProps {
  interactions: PopulatedInteraction[];
  currentEmployeeId: string;
  lastOneOnOneDate?: string | null;
}

type EmployeeInfo = {
  name: string;
  imageUrl?: string;
};

type EmployeeMap = Map<string, EmployeeInfo>;

const interactionRoute = (interaction: PopulatedInteraction): string => {
  switch (interaction.type) {
    case InteractionType.PRAISE:
      return `/interactions/praise/${interaction._id}`;
    case InteractionType.FEEDBACK:
      return `/interactions/feedback/${interaction._id}`;
    case InteractionType.DEBRIEF:
    default:
      return `/interactions/debrief/${interaction._id}`;
  }
};

export const GoalInteractionsList = ({
  interactions,
  currentEmployeeId,
  lastOneOnOneDate,
}: GoalInteractionsListProps) => {
  const { t, i18n } = useTranslation('oneOnOne');
  const navigate = useNavigate();
  const { data: employees } = useEmployees({ isActive: true });
  const [selectedPraiseId, setSelectedPraiseId] = useState<string | null>(null);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(
    null
  );

  // Create employee map for quick lookup
  const employeeMap = useMemo<EmployeeMap>(() => {
    const map = new Map();
    employees?.forEach((emp) => {
      map.set(emp._id, {
        name: `${emp.personalInfo.firstName} ${emp.personalInfo.lastName}`,
        imageUrl: emp.personalInfo.imageUrl,
      });
    });
    return map;
  }, [employees]);

  const filteredAndSortedInteractions = useMemo(() => {
    const filtered = interactions.filter((interaction) => {
      // Check if populated employee _id matches currentEmployeeId
      const creatorId =
        typeof interaction.creatorId === 'string'
          ? interaction.creatorId
          : interaction.creatorId._id;

      if (creatorId === currentEmployeeId) {
        return true;
      }

      if (
        interaction.type === InteractionType.PRAISE ||
        interaction.type === InteractionType.FEEDBACK
      ) {
        if ('receiverId' in interaction) {
          const receiverId =
            typeof interaction.receiverId === 'string'
              ? interaction.receiverId
              : interaction.receiverId._id;
          return receiverId === currentEmployeeId;
        }
      }

      if (interaction.type === InteractionType.DEBRIEF) {
        const core =
          'coreParticipants' in interaction
            ? interaction.coreParticipants ?? []
            : [];
        const others =
          'otherParticipants' in interaction
            ? interaction.otherParticipants ?? []
            : [];
        // For debrief, participants are arrays of populated employees
        const allParticipantIds = [...core, ...others].map((p) =>
          typeof p === 'string' ? p : p._id
        );
        return allParticipantIds.includes(currentEmployeeId);
      }

      return false;
    });

    // Sort: blockages first, then by creation date (newest first)
    return filtered.sort((a, b) => {
      const aIsBlockage = isOpenBlockage(a);
      const bIsBlockage = isOpenBlockage(b);

      // Blockages come first
      if (aIsBlockage && !bIsBlockage) return -1;
      if (!aIsBlockage && bIsBlockage) return 1;

      // If both are blockages or both are not, sort by date (newest first)
      const dateA = new Date(a.metadata.createdAt).getTime();
      const dateB = new Date(b.metadata.createdAt).getTime();
      return dateB - dateA;
    });
  }, [interactions, currentEmployeeId]);

  const getSenderReceiverInfo = (interaction: PopulatedInteraction) => {
    const creatorId =
      typeof interaction.creatorId === 'string'
        ? interaction.creatorId
        : interaction.creatorId._id;
    const creator = employeeMap.get(creatorId);
    const creatorName = creator?.name || t('unknown');

    if (interaction.type === InteractionType.DEBRIEF) {
      const core =
        'coreParticipants' in interaction
          ? interaction.coreParticipants ?? []
          : [];
      const others =
        'otherParticipants' in interaction
          ? interaction.otherParticipants ?? []
          : [];
      const allParticipantIds = [...core, ...others]
        .map((p) => (typeof p === 'string' ? p : p._id))
        .filter((id) => id !== creatorId);

      const participantInfos = allParticipantIds
        .map((id) => employeeMap.get(id))
        .filter(Boolean);

      return {
        sender: creatorName,
        senderInfo: creator,
        receiver:
          allParticipantIds.length > 0
            ? `${
                allParticipantIds.length > 1 ? allParticipantIds.length : ''
              } ${
                allParticipantIds.length === 1
                  ? t('participant')
                  : t('participants')
              }`
            : t('unknown'),
        receiverInfo: participantInfos[0], // Show first participant's avatar
        isDebrief: true,
        participantCount: allParticipantIds.length,
      };
    } else {
      const receiverId =
        'receiverId' in interaction
          ? typeof interaction.receiverId === 'string'
            ? interaction.receiverId
            : interaction.receiverId._id
          : '';
      const receiver = employeeMap.get(receiverId);
      const receiverName = receiver?.name || t('unknown');

      return {
        sender: creatorName,
        senderInfo: creator,
        receiver: receiverName,
        receiverInfo: receiver,
        isDebrief: false,
      };
    }
  };

  if (!filteredAndSortedInteractions.length) {
    return (
      <div className="py-6 text-center text-muted-foreground">
        {t('noInteractions')}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-700 text-start">
          {t('previousInteractions')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filteredAndSortedInteractions.map((interaction) => {
            const { sender, senderInfo, receiver, receiverInfo, isDebrief } =
              getSenderReceiverInfo(interaction);
            const isNew = isNewInteraction(interaction, lastOneOnOneDate);
            const isBlockage = isOpenBlockage(interaction);

            return (
              <div
                key={interaction._id}
                onClick={() => {
                  if (interaction.type === InteractionType.PRAISE) {
                    setSelectedPraiseId(interaction._id);
                  } else if (interaction.type === InteractionType.FEEDBACK) {
                    setSelectedFeedbackId(interaction._id);
                  } else {
                    navigate({ to: interactionRoute(interaction) });
                  }
                }}
                className={cn(
                  'relative bg-white rounded-lg border border-gray-200',
                  'p-5 transition-all duration-200 cursor-pointer',
                  'hover:border-gray-300 hover:shadow-md',
                  'group',
                  isBlockage && 'border-amber-300 bg-amber-50/30'
                )}
              >
                {/* New Badge - only show if not a blockage */}
                {isNew && !isBlockage && (
                  <Badge
                    variant="default"
                    className="absolute top-2 end-2 bg-green-600 hover:bg-green-700 text-white border-0 z-10"
                  >
                    {t('new')}
                  </Badge>
                )}
                <div className="space-y-3">
                  {/* Main Content: Avatars with badge in middle */}
                  <div className="flex items-center justify-center gap-4">
                    {/* Sender Avatar with Name */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-2">
                      <div className="relative">
                        <div className="border-2 border-gray-200 rounded-full shadow-sm group-hover:border-gray-300 transition-colors">
                          <UserSelectorAvatar
                            fullName={senderInfo?.name || t('unknown')}
                            imageUrl={senderInfo?.imageUrl}
                            size="md"
                          />
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-foreground text-center max-w-[90px] truncate">
                        {sender}
                      </span>
                    </div>

                    {/* Arrow to Badge */}
                    <div className="flex-shrink-0">
                      <ArrowRight className="h-4 w-4 text-muted-foreground/60 rtl:rotate-180" />
                    </div>

                    {/* Badge in the middle */}
                    <div className="flex-shrink-0">
                      <InteractionTypeBadge
                        type={interaction.type}
                        isDraft={interaction.isDraft}
                      />
                    </div>

                    {/* Arrow to Receiver */}
                    <div className="flex-shrink-0">
                      <ArrowRight className="h-4 w-4 text-muted-foreground/60 rtl:rotate-180" />
                    </div>

                    {/* Receiver Avatar with Name */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-2">
                      {isDebrief ? (
                        <>
                          <div className="relative">
                            {receiverInfo ? (
                              receiverInfo.imageUrl ? (
                                <img
                                  src={receiverInfo.imageUrl}
                                  alt={receiverInfo.name}
                                  className="w-14 h-14 rounded-full object-cover border-2 border-gray-200 shadow-sm group-hover:border-gray-300 transition-colors"
                                />
                              ) : (
                                <div className="border-2 border-gray-200 rounded-full shadow-sm group-hover:border-gray-300 transition-colors">
                                  <UserSelectorAvatar
                                    fullName={receiverInfo.name}
                                    imageUrl={undefined}
                                    size="md"
                                  />
                                </div>
                              )
                            ) : (
                              <div className="w-14 h-14 rounded-full border-2 border-gray-200 bg-gray-50 flex items-center justify-center shadow-sm group-hover:border-gray-300 transition-colors">
                                <Users className="h-7 w-7 text-muted-foreground/60" />
                              </div>
                            )}
                          </div>
                          <span className="text-xs font-semibold text-foreground text-center max-w-[120px] truncate">
                            {receiver}
                          </span>
                        </>
                      ) : (
                        <>
                          <div className="relative">
                            <div className="border-2 border-gray-200 rounded-full shadow-sm group-hover:border-gray-300 transition-colors">
                              <UserSelectorAvatar
                                fullName={receiverInfo?.name || t('unknown')}
                                imageUrl={receiverInfo?.imageUrl}
                                size="md"
                              />
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-foreground text-center max-w-[120px] truncate">
                            {receiver}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Date at the bottom */}
                  <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="whitespace-nowrap">
                      {formatDefaultDate(
                        interaction.metadata.createdAt,
                        i18n.language as 'en' | 'he'
                      )}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedPraiseId && (
        <PraiseReviewDrawer
          isOpen={!!selectedPraiseId}
          praiseId={selectedPraiseId}
          onClose={() => setSelectedPraiseId(null)}
        />
      )}

      {selectedFeedbackId && (
        <FeedbackReviewDrawer
          isOpen={!!selectedFeedbackId}
          feedbackId={selectedFeedbackId}
          onClose={() => setSelectedFeedbackId(null)}
        />
      )}
    </>
  );
};
